<template>
  <div>
    <PageAllow controller="NewProductionPlans" action="index" />
    <Breadcrumbs :items="items" />

    <v-sheet class="transparent-background-color">
      <v-row dense class="mx-5">
        <v-col cols="12" sm="12" md="4">
          <v-row dense align="center" class="pa-1">
            <SearcherDatetimeRange
              type="datetime"
              v-model="datetimeRage"
              :label="$t('productionJobsNew.actions.staleUploadTime')"
              removeIcon
            />
          </v-row>
        </v-col>
        <v-col cols="12" sm="12" md="8" align-self="center">
          <v-row dense align="center" class="search-controls">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="px-0 py-0 caption font-weight-bold grey--text"
                  color="primary"
                  @click.stop="onSearch()"
                >
                  <v-icon small class="pe-1">fa-search</v-icon>
                  {{ $t("Search.search") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.searchTip") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="caption font-weight-bold grey--text"
                  @click.stop="resetSearch()"
                >
                  <v-icon x-small class="pe-1">fa-trash-alt</v-icon>
                  {{ $t("Search.reset") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.resetTip") }}</span>
            </v-tooltip>
            <v-divider vertical />
            <v-btn
              class="caption font-weight-bold grey--text"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
              small
              text
              @click.stop="$store.commit('toggleSearchPanel')"
            >
              {{ $t("Search.pro") }}
              <v-icon small class="ps-1">{{
                $store.state.showSearchPanel ? "fa-angle-up" : "fa-angle-down"
              }}</v-icon>
            </v-btn>
            <v-spacer />
          </v-row>
        </v-col>
      </v-row>
      <v-expansion-panels
        v-model="$store.getters.panels"
        multiple
        flat
        accordion
      >
        <v-expansion-panel class="transparent-background-color">
          <v-expansion-panel-content>
            <v-row dense align="center">
              <v-col cols="12" sm="6" md="4">
                <SearcherKeyword
                  ref="job_code"
                  v-model="options.job_code"
                  @text="staleUploadsKeyWord = $event"
                  :label="$t('productionJobsNew.productionJobsKeyWord')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherAccountUser
                  v-model="executorUserSelected"
                  :type="[1, 2]"
                  :label="$t('productionJobsNew.Fields.executor_name')"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <SearcherStatus
                  v-model="options.stale_audit_status"
                  :items="staleStatusItems"
                  :label="$t('productionJobsNew.actions.staleAuditStatus')"
                />
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <div class="mx-4 mb-4">
        <v-data-table
          :headers="headers"
          :items="stale_jobs"
          :options.sync="options"
          :page.sync="paging.page"
          :server-items-length="paging.count"
          :loading="loading"
          multi-sort
          class="elevation-2 px-4 pt-1 sticky-table"
          id="sticky-table"
          :class="stickTableCols ? 'sticky-first-col sticky-last-col' : ''"
          fixed-header
          :footer-props="$t('$config.footer-props')"
        >
          <template v-slot:header.actions="{ header }">
            {{ header.text }}
            <v-icon x-small>fa-sliders-h</v-icon>
          </template>
          <template v-slot:item.job_code="{ item, index }">
            <span
              class="text-no-wrap caption font-weight-bold"
              :v-resize="!index ? $stickTableResizer() : null"
            >
              {{ item.job_code }}
            </span>
            <br />
            <v-chip x-small label color="danger" text-color="white">
              {{ $t("productionJobsNew.staleStatus.canceledOrTruncated") }}
            </v-chip>
          </template>
          <template v-slot:item.plan_num="{ item }">
            <span class="text-no-wrap caption">
              {{ $t("productionJobsNew.Fields.plan_num") }}
              {{ parseFloat(item.plan_num) }}
            </span>
            <br />
            <span class="text-no-wrap caption">
              {{ $t("productionJobsNew.Fields.actual_num") }}
              {{ parseFloat(item.actual_num) }}
            </span>
          </template>
          <template v-slot:item.stale_upload_num="{ item }">
            <span class="red--text font-weight-bold">{{
              parseFloat(item.stale_upload_num)
            }}</span>
          </template>
          <template v-slot:item.stale_upload_time="{ item }">
            <span class="text-no-wrap caption">{{
              item.stale_upload_time | formatTime
            }}</span>
          </template>
          <template v-slot:item.stale_audit_status="{ item }">
            <v-chip
              small
              label
              :color="staleAuditStatusColor(item.stale_audit_status)"
              text-color="white"
            >
              {{ staleAuditStatusText(item.stale_audit_status) }}
            </v-chip>
          </template>
          <template v-slot:item.weigh_details_json="{ item }">
            <v-tooltip bottom max-width="400" v-if="weighList(item).length">
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  small
                  label
                  outlined
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                >
                  {{
                    $t("productionJobsNew.weighCount", [weighList(item).length])
                  }}
                </v-chip>
              </template>
              <div
                v-for="(weigh, index) in weighList(item)"
                :key="index"
                class="caption"
              >
                {{ weigh.weigh_time | formatTime }} ｜
                {{ $t("productionJobsNew.Fields.net_weight") }}
                {{ weigh.net_weight }} /
                {{ $t("productionJobsNew.Fields.gross_weight") }}
                {{ weigh.gross_weight }}
              </div>
            </v-tooltip>
            <span v-else class="caption">-</span>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-tooltip bottom v-if="item.stale_audit_status == 1">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  x-small
                  depressed
                  color="accent"
                  v-bind="attrs"
                  v-on="on"
                  @click.stop="openAudit(item)"
                >
                  <v-icon x-small class="pe-1">fa-user-check</v-icon>
                  {{ $t("productionJobsNew.actions.auditAction") }}
                </v-btn>
              </template>
              <span>{{
                $t("productionJobsNew.actions.auditStaleUploadTip")
              }}</span>
            </v-tooltip>
            <span v-else class="caption">-</span>
          </template>
        </v-data-table>
      </div>
    </v-sheet>

    <ProductionJobAuditStaleAlert
      v-model="auditAlert"
      :job="auditJob"
      @done="getStaleUploadsFromAPI()"
    />
  </div>
</template>
<script>
import i18n from "@/i18n";
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import { mapState } from "vuex";
import NewProductionJobs from "@/api/new_production_jobs";
import SearcherDatetimeRange from "@/components/plugins/SearcherDatetimeRange";
import SearcherKeyword from "@/components/plugins/SearcherKeyword";
import SearcherAccountUser from "@/components/plugins/SearcherAccountUser";
import SearcherStatus from "@/components/plugins/SearcherStatus";
import ProductionJobAuditStaleAlert from "./plugins/AuditStaleAlert";

const defaultOptions = {
  start_time:
    new Date(new Date().getTime() - 30 * 24 * 3600 * 1000)
      .toISOString()
      .substr(0, 10) + " 00:00:00",
  end_time: new Date().toISOString().substr(0, 10) + " 23:59:59",
  job_code: "",
  executor_user_id: 0,
  stale_audit_status: [1],
};

export default {
  components: {
    PageAllow,
    Breadcrumbs,
    SearcherDatetimeRange,
    SearcherKeyword,
    SearcherAccountUser,
    SearcherStatus,
    ProductionJobAuditStaleAlert,
  },

  computed: {
    items: () => [
      {
        text: i18n.t("Nav.home"),
        disabled: false,
        href: "/home",
      },
      {
        text: i18n.t("Nav.productionNewManagement"),
        disabled: false,
        replace: true,
        href: "/production-plans-new",
      },
      {
        text: i18n.t("Nav.productionJobsStaleUploads"),
        disabled: true,
        href: "/production-jobs-stale-uploads",
      },
    ],

    // 迟滞审核状态筛选项（默认仅待文员审核）
    staleStatusItems() {
      return [
        {
          value: 1,
          text: i18n.t("productionJobsNew.staleStatus.pending"),
          color: "warning",
          textColor: "white",
        },
        {
          value: 2,
          text: i18n.t("productionJobsNew.staleStatus.approved"),
          color: "success",
          textColor: "white",
        },
        {
          value: 3,
          text: i18n.t("productionJobsNew.staleStatus.rejected"),
          color: "danger",
          textColor: "white",
        },
      ];
    },

    headers() {
      return [
        {
          text: i18n.t("productionJobsNew.Fields.job_code"),
          align: "start",
          sortable: false,
          value: "job_code",
        },
        {
          text: i18n.t("productionJobsNew.Fields.executor_name"),
          align: "start",
          sortable: false,
          value: "executor_name",
        },
        {
          text:
            i18n.t("productionJobsNew.Fields.plan_num") +
            " / " +
            i18n.t("productionJobsNew.Fields.actual_num"),
          align: "center",
          sortable: false,
          value: "plan_num",
        },
        {
          text: i18n.t("productionJobsNew.actions.staleUploadNum"),
          align: "center",
          sortable: false,
          value: "stale_upload_num",
        },
        {
          text: i18n.t("productionJobsNew.actions.staleUploadTime"),
          align: "start",
          sortable: false,
          value: "stale_upload_time",
        },
        {
          text: i18n.t("productionJobsNew.actions.staleAuditStatus"),
          align: "center",
          sortable: false,
          value: "stale_audit_status",
        },
        {
          text: i18n.t("productionJobsNew.actions.staleOfflineDetails"),
          align: "start",
          sortable: false,
          value: "weigh_details_json",
        },
        {
          text: i18n.t("TableHeaders.actions"),
          align: "start",
          sortable: false,
          value: "actions",
        },
      ];
    },

    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading", "settings", "stickTableCols"]),
  },

  data: () => ({
    stale_jobs: [],
    paging: {},
    options: JSON.parse(
      sessionStorage.getItem("production-jobs-stale-uploads-options")
        ? sessionStorage.getItem("production-jobs-stale-uploads-options")
        : JSON.stringify(defaultOptions)
    ),
    staleUploadsKeyWord: "",
    executorUserSelected: null,
    auditAlert: false,
    auditJob: null,
    datetimeRage: [
      new Date(new Date().getTime() - 30 * 24 * 3600 * 1000)
        .toISOString()
        .substr(0, 10) + " 00:00:00",
      new Date().toISOString().substr(0, 10) + " 23:59:59",
    ],
  }),

  mounted() {
    this.$commonUtils
      .loadOptions("production-jobs-stale-uploads", ["executorUserSelected"])
      .then(
        function (data) {
          if (data.options) {
            this.options = data.options;
            if (
              typeof this.options.start_time != "undefined" &&
              typeof this.options.end_time != "undefined"
            )
              this.datetimeRage = [
                this.options.start_time,
                this.options.end_time,
              ];
          } else this.resetSearch();

          data.selecteds.forEach((selected) => {
            switch (selected.key) {
              case "executorUserSelected":
                this.executorUserSelected = selected.value;
                break;
            }
          });
        }.bind(this)
      );
  },

  methods: {
    resetSearch() {
      this.executorUserSelected = null;
      this.datetimeRage = [
        new Date(new Date().getTime() - 30 * 24 * 3600 * 1000)
          .toISOString()
          .substr(0, 10) + " 00:00:00",
        new Date().toISOString().substr(0, 10) + " 23:59:59",
      ];
      this.options = JSON.parse(JSON.stringify(defaultOptions));
      [this.$refs.job_code].forEach((k) => {
        if (k) k.clear();
      });
    },
    onSearch() {
      this.options = Object.assign({}, this.options, {
        job_code: this.staleUploadsKeyWord,
      });
    },
    weighList(item) {
      return item && Array.isArray(item.weigh_details_json)
        ? item.weigh_details_json
        : [];
    },
    staleAuditStatusColor(status) {
      const colors = { 0: "grey", 1: "warning", 2: "success", 3: "danger" };
      return colors[status] ? colors[status] : "grey";
    },
    staleAuditStatusText(status) {
      const statusKeys = {
        0: "none",
        1: "pending",
        2: "approved",
        3: "rejected",
      };
      return i18n.t(
        "productionJobsNew.staleStatus." + (statusKeys[status] || "none")
      );
    },
    openAudit(item) {
      this.auditJob = item;
      this.auditAlert = true;
    },
    getStaleUploadsFromAPI() {
      var options = JSON.parse(JSON.stringify(this.options));
      // 后端审核状态仅支持单值筛选，多选时取首个；清空则查询全部状态
      options.stale_audit_status =
        Array.isArray(options.stale_audit_status) &&
        options.stale_audit_status.length > 0
          ? String(options.stale_audit_status[0])
          : "";
      NewProductionJobs.staleUploads(options).then(
        function (response) {
          if (response.data.status.isOK()) {
            const data = response.data.data || {};
            this.stale_jobs = data.list || [];
            this.paging = {
              page: this.options.page ? this.options.page : 1,
              count: data.total ? data.total : 0,
            };
          } else if (response.data.error.code.isNoResultSet()) {
            this.options.page = 1;
          }
        }.bind(this)
      );
    },
  },

  watch: {
    options: {
      deep: true,
      handler() {
        this.getStaleUploadsFromAPI();
        sessionStorage.setItem(
          "production-jobs-stale-uploads-options",
          JSON.stringify(this.options)
        );
      },
    },
    executorUserSelected: {
      handler() {
        this.options.executor_user_id =
          this.executorUserSelected && this.executorUserSelected.id
            ? this.executorUserSelected.id
            : 0;
        sessionStorage.setItem(
          "production-jobs-stale-uploads-executorUserSelected",
          JSON.stringify(this.executorUserSelected)
        );
      },
    },
    datetimeRage: {
      handler() {
        if (this.datetimeRage[0] == null || this.datetimeRage[0] == "") {
          this.options.start_time =
            new Date(new Date().getTime() - 30 * 24 * 3600 * 1000)
              .toISOString()
              .substr(0, 10) + " 00:00:00";
        } else {
          this.options.start_time = this.datetimeRage[0];
        }

        if (this.datetimeRage[1] == null || this.datetimeRage[1] == "") {
          this.options.end_time =
            new Date().toISOString().substr(0, 10) + " 23:59:59";
        } else {
          this.options.end_time = this.datetimeRage[1];
        }
      },
    },
  },
};
</script>
